// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import constants from "@store/constants";
import hasSameParams from "@store/Framework/Common/Utilities/Events/hasSameParams";
import { getEventParameters } from "@store/Framework/Store/Store/Pages/EventList/EventListSelector";
import getEventList, {
    GetEventsApiParameter,
} from "@store/Framework/Store/Store/Pages/EventList/Handlers/GetEventList";
import EventAddress from "@store/Library/Components/Events/Common/EventAddress";
import EventCategories, { EventCategoriesStyles } from "@store/Library/Components/Events/Common/EventCategories";
import EventDate from "@store/Library/Components/Events/Common/EventDate";
import EventDescription from "@store/Library/Components/Events/Common/EventDescription";
import EventImage from "@store/Library/Components/Events/Common/EventImage";
import EventLink, { EventLinkStyles } from "@store/Library/Components/Events/Common/EventLink";
import EventTitle from "@store/Library/Components/Events/Common/EventTitle";
import { EventListTileStyles } from "@store/Library/Components/Events/EventListTile";
import { carouselProps } from "@store/Themes/common/props/carousel";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    autoplay = "autoplay",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.autoplay]: number;
        [fields.title]: string;
    };
}

export interface EventListFeaturedStyles {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    tileStyle?: EventListTileStyles;
    tileWrapper?: GridContainerProps;
    imageGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideWrapperStyles?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
    eventCategoriesStyle?: EventCategoriesStyles;
    eventLinkStyle?: EventLinkStyles;
}

const carouselArrowSize = css`
    ${breakpointValues([`1em`, null, `${pxToRem(50)}`], "width")}
    ${breakpointValues([`1em`, null, `${pxToRem(50)}`], "height")}
`;

const eventListFeaturedStyles: EventListFeaturedStyles = {
    title: {
        variant: "h2",
        css: css`
            margin: 0 0 ${pxToRem(30)};
        `,
    },
    wrapper: {
        css: css`
            ${breakpointValues([`${pxToRem(30)}`, null, `${pxToRem(40)}`], "margin-top")}
        `,
    },
    tileStyle: {
        wrapper: {
            css: css`
                &:first-child {
                    padding-top: 0;
                }
            `,
        },
    },
    tileWrapper: {
        gap: 0,
    },
    imageGridItem: {
        width: [12, 12, 7, 8, 8],
    },
    contentGridItem: {
        width: [12, 12, 5, 4, 4],
        css: css`
            flex-direction: column;
            background-color: ${getColor("primary")};

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding"])}
            `}
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideWrapperStyles: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
        `,
    },
    prevArrowWrapper: carouselProps.prevArrowWrapper,
    prevArrowButton: {
        ...carouselProps.prevArrowButton,
        css: css`
            ${carouselProps.prevArrowButton?.css}
            ${carouselArrowSize}
        `,
    },
    dotsContainerWrapper: carouselProps.dotsWrapper,
    dotButton: carouselProps.dotButton,
    dotButtonSelected: carouselProps.dotButtonSelected,
    nextArrowWrapper: carouselProps.nextArrowWrapper,
    nextArrowButton: {
        ...carouselProps.nextArrowButton,
        css: css`
            ${carouselProps.nextArrowButton?.css}
            ${carouselArrowSize}
        `,
    },
    eventCategoriesStyle: {
        tagWrapper: {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${breakpointValues([`${pxToRem(5)}`, null, `${pxToRem(20)}`], "margin-bottom")}
                    `,
                },
            },
        },
    },
    eventLinkStyle: {
        link: {
            css: css`
                ${breakpointValues([`${pxToRem(15)}`, null, `${pxToRem(30)}`], "margin-top")}
            `,
        },
    },
};

const styles = eventListFeaturedStyles;

const EventListFeatured: FC<OwnProps> = ({ fields }) => {
    const featuredParameter = {
        isFeatured: true,
    };
    const parameter = useAppSelector(state => getEventParameters(state));
    const dispatch = useDispatch();

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const [apiParameter, setApiParameter] = useState<GetEventsApiParameter>({ ...featuredParameter, ...parameter });
    const baseEventListDataView = useAppSelector(state => getDataView(state.data.pages, apiParameter));
    const eventListDataView = usePersistentDataView(baseEventListDataView);
    const { isLoading, value: eventList } = eventListDataView;
    const currentPage = useAppSelector(state => getPageLinkByNodeId(state, getCurrentPage(state).nodeId));

    useEffect(() => {
        if (!hasSameParams(apiParameter, parameter)) {
            setApiParameter({ ...featuredParameter, ...parameter });
        }
    }, [parameter]);

    useEffect(() => {
        if (!isLoading || !eventList) {
            dispatch(getEventList(apiParameter));
        }
    }, [apiParameter]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !eventList || eventList.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, eventList]);

    if (!eventList && isLoading) {
        return null;
    }

    const renderTile = (page: PageProps) => {
        const {
            fields: {
                title,
                urlSegment,
                startDate,
                endDate,
                hideHourMinutes,
                dateTimezone,
                isLocalBranch,
                localBranchTitle,
                addressWrapper,
                previewImage,
                categoriesList,
                eventSummary,
                linkLabel,
            },
        } = page;

        const eventLink = `${currentPage?.url}/${urlSegment}`;

        return (
            <GridContainer {...styles.tileWrapper}>
                <GridItem {...styles.imageGridItem}>
                    <EventImage
                        previewImage={previewImage}
                        altText={title}
                        eventLink={eventLink}
                        startDate={startDate}
                        endDate={endDate}
                        aspectRatio="3/2"
                        mobileAspectRatio="4/3"
                    />
                </GridItem>
                <GridItem {...styles.contentGridItem}>
                    <EventCategories categoriesList={categoriesList} extendedStyles={styles.eventCategoriesStyle} />
                    <Hidden above="sm">
                        <EventTitle
                            title={title}
                            eventLink={eventLink}
                            variant="h3"
                            forwardAs={!fields.title ? "h2" : "h3"}
                            lightText
                        />
                    </Hidden>
                    <Hidden below="md">
                        <EventTitle
                            title={title}
                            eventLink={eventLink}
                            variant="h2"
                            forwardAs={!fields.title ? "h2" : "h3"}
                            lightText
                        />
                    </Hidden>
                    <EventDescription eventSummary={eventSummary} lightText />
                    <EventDate
                        startDate={startDate}
                        endDate={endDate}
                        dateTimezone={dateTimezone}
                        hideHourMinutes={hideHourMinutes}
                        lightText
                    />
                    <EventAddress
                        addressWrapper={addressWrapper}
                        isLocalBranch={isLocalBranch}
                        localBranchTitle={localBranchTitle}
                        lightText
                    />
                    <EventLink
                        eventLink={eventLink}
                        linkLabel={linkLabel}
                        lightText
                        extendedStyles={styles.eventLinkStyle}
                    />
                </GridItem>
            </GridContainer>
        );
    };

    return (
        <DataViewLoadingOverlay dataView={eventListDataView} {...styles.loadingOverlay}>
            {eventList && eventList.length > 0 && (
                <StyledWrapper {...styles.wrapper}>
                    {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
                    {eventList.length > 1 && (
                        <StyledWrapper {...styles.prevArrowWrapper}>
                            <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                                <VisuallyHidden>{translate("Previous slide")}</VisuallyHidden>
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.slideContainerWrapper}>
                            {eventList.map((page: PageProps) => (
                                <StyledWrapper key={`slide_${page.id}`} {...styles.slideWrapperStyles}>
                                    {renderTile(page)}
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                    {eventList.length > 1 && (
                        <>
                            <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                                {eventList.map((_, i) => {
                                    return (
                                        <Button
                                            className="slide-dot-button"
                                            key={`slide-dot-${_.id}`}
                                            {...(i === selectedIndex ? styles.dotButtonSelected : styles.dotButton)}
                                            onClick={() => embla && embla.scrollTo(i)}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                            <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                                <Button
                                    className="slide-arrow"
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                >
                                    <VisuallyHidden>{translate("Next slide")}</VisuallyHidden>
                                </Button>
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: EventListFeatured,
    definition: {
        group: constants.widgetGroup.eventList,
        displayName: "Event List Featured",
        allowedContexts: ["EventListPage"] as any,
        fieldDefinitions: [
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                min: 1,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Featured Events",
                fieldType: "Translatable",
                placeholder: "Input a title",
            },
        ],
    },
};

export default widgetModule;
